import { collection, deleteDoc, doc, serverTimestamp, updateDoc, writeBatch } from 'firebase/firestore'
import type { User } from 'firebase/auth'
import { db } from '../lib/firebase'

/**
 * Creating an organisation is free and ungated by design. Tier-gating it would
 * guard the wrong resource: an organisation with no dashboards and no ingestion
 * costs nothing, and ingestion is guarded separately.
 *
 * Structurally identical to the personal organisation created at sign-up — only
 * `kind` differs, and that exists solely so the UI can label one "Personal".
 */
export async function createOrganisation(name: string, user: User): Promise<string> {
  const orgRef = doc(collection(db, 'orgs'))
  const batch = writeBatch(db)

  batch.set(orgRef, {
    name: name.trim() || 'Untitled organisation',
    kind: 'shared',
    primaryOwnerUid: user.uid,
    createdAt: serverTimestamp(),
  })
  // Written in the same batch as the org: the security rule uses getAfter() to
  // confirm the creator is the primary owner of the org this batch just made,
  // which is what stops it granting membership of anyone else's.
  batch.set(doc(db, 'orgs', orgRef.id, 'members', user.uid), {
    uid: user.uid,
    role: 'owner',
    displayName: user.displayName,
    email: user.email,
    joinedAt: serverTimestamp(),
  })

  await batch.commit()
  return orgRef.id
}

export async function renameOrganisation(orgId: string, name: string): Promise<void> {
  await updateDoc(doc(db, 'orgs', orgId), { name: name.trim() || 'Untitled organisation' })
}

/** Leaving is removing your own membership. The primary owner cannot, since an
 *  organisation must always have a responsible party. */
export async function leaveOrganisation(orgId: string, uid: string): Promise<void> {
  await deleteDoc(doc(db, 'orgs', orgId, 'members', uid))
}
